// The desk: MoneyHQ's agent, docked on every page (⌘J) and on its own page (/desk). It reads and drives
// the page through the page's tools (see lib/desk/tools), so it can say what the chart on screen is
// forming, switch timeframes, and draw a paper plan's levels; its tools' results come back as cards
// in the conversation (desk-cards). On wide screens the panel docks beside the page; on smaller ones
// it overlays. The conversation itself (useDeskChat) is shared by the panel and the page.
import { Fragment, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ArrowUpIcon, HistoryIcon, Maximize2Icon, SquareIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { NSE, positionSize, type Session } from '@planb/core';
import { marketIndex, marketReading } from '@/lib/desk/market';
import { api } from '@/lib/api';
import { ask, type DeskCardData, type DeskEvent } from '@/lib/desk/client';
import { openStock, runPageTool, setNavigate, usePageTools } from '@/lib/desk/tools';
import { usePaperTrades } from './paper-trades';
import { actionLabel, type CorpAction } from '@/lib/corp-actions';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DeskCard, type CardActions, type ChartProps } from './desk-cards';
import { DeskIcon } from './market-ui';

// ---- Open/close from anywhere (the chart's Ask button opens it with a question).
let open = false;
let queued: string | null = null;
const subs = new Set<() => void>();
const emit = () => { for (const s of subs) s(); };
export function openDesk(question?: string) { open = true; if (question) queued = question; emit(); }
export function closeDesk() { open = false; emit(); }
const useOpen = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => open);
/** A question handed to the desk page (the panel's "open full page" carries none; a card can). */
export const takeQueued = () => { const q = queued; queued = null; return q; };

type Msg =
  | { role: 'user'; text: string }
  | { role: 'desk'; text: string; steps: string[]; error?: string; done: boolean; cards?: Proposal[]; ask?: CapitalAsk; parts?: DeskCardData[] };

/** A paper trade the desk proposed: placed only if the user clicks Place. */
interface Proposal {
  id: string;
  a: { symbol: string; side: 'long' | 'short'; entry_type: 'limit' | 'stop' | 'market'; entry: number; stop: number; target: number; qty: number; horizon: 'intraday' | 'swing'; setup: string; tested: string; trailing?: boolean; sizedBy?: string; exNote?: string };
  size: { atRisk: number; value: number; rr: number; capital: number; riskPct: number; capped: boolean };
  state: 'pending' | 'placing' | 'placed' | 'dismissed';
  error?: string;
}
type DeskMsg = Extract<Msg, { role: 'desk' }>;
/** Asked inline when a plan needs the capital and risk the user hasn't saved yet. */
interface CapitalAsk { id: string; done: boolean }

const STEP_LABEL: Record<string, (a?: Record<string, unknown>) => string> = {
  read_page: () => 'Reading your screen',
  read_market: () => 'Reading the market board',
  show_chart: (a) => `Opening ${[a?.symbol, a?.timeframe].filter(Boolean).join(' · ') || 'the chart'}`,
  mark_chart: () => 'Marking the plan on the chart',
  plan_paper_trade: () => 'Sizing the plan and drawing it on the chart',
  paper_trades: () => 'Checking your paper trades',
  company_filings: (a) => `Reading ${a?.symbol ?? 'the company'}’s NSE filings`,
  news_headlines: () => 'Checking press headlines',
  company_valuation: (a) => `Reading ${a?.symbol ?? 'the'} valuation from NSE`,
  quarterly_results: (a) => `Reading ${a?.symbol ?? 'the company'}’s quarterly results`,
  shareholding: () => 'Checking the shareholding pattern',
  board_meetings: () => 'Checking upcoming board meetings',
  review_paper_trade: () => 'Replaying your trade on the real candles',
  first_trade_guide: (a) => `Lesson: step ${a?.step ?? 1} of 5`,
  momentum_portfolio: () => 'Reading your long-term portfolio',
  simulate_momentum: (a) => `Replaying the momentum basket from ${a?.from ?? 'that date'}`,
  test_level: (a) => `Testing ${a?.symbol ?? 'the stock'} at ${a?.level ?? 'that level'}`,
  best_setups: () => 'Scanning the NSE 500 for the best-tested setups',
  momentum_top: () => 'Fetching this week’s momentum top 20',
  size_position: () => 'Sizing the position',
  momentum_rank: () => 'Checking the momentum rank',
  market_mood: () => 'Reading India VIX, valuations and breadth',
  corporate_actions: (a) => `Checking ${a?.symbol ?? 'the stock'}’s corporate actions`,
  candle_check: () => 'Checking six years of 5-minute candles',
  tested_status: () => 'Checking which are in a tested setup',
  vs_index: (a) => `Comparing ${a?.symbol ?? 'the stock'} with its index`,
};

// NSE's MCP tools (get_market_mood …): "Checking NSE data: market mood".
const NSE_TOOL = /^(get|compare|nse_get|cm_get)_/;
const stepLabel = (name: string, args?: Record<string, unknown>) =>
  STEP_LABEL[name]?.(args) ?? (NSE_TOOL.test(name) ? `Checking NSE data: ${name.replace(NSE_TOOL, '').replace(/_/g, ' ')}` : name);
const inrText = (v: number) => `₹${v.toLocaleString('en-IN', Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct2 = (v: number) => Math.round(v * 100) / 100;
/** The guided first trade's opening message (the board's "New here?" strip sends it too). */
export const GUIDE_START = 'Guide me through my first paper trade, step by step';

const newId = () => (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** The open conversation, remembered in this browser so a reload reopens it (the server keeps it). */
const THREAD_KEY = 'mhq.deskThread';
const storedThread = () => { try { return localStorage.getItem(THREAD_KEY); } catch { return null; } };
const rememberThread = (id: string) => { try { localStorage.setItem(THREAD_KEY, id); } catch { /* not stored */ } };
export interface ThreadRow { id: string; title: string; updatedAt: string }
/** The conversation list as last loaded, kept across pages (refreshed on each open). */
let keptThreads: ThreadRow[] | null = null;
type SavedMsg = { role: 'user' | 'desk'; text: string; components?: DeskCardData[] };
const toMsgs = (ms: SavedMsg[]): Msg[] =>
  ms.map((m) => (m.role === 'user' ? { role: 'user', text: m.text } : { role: 'desk', text: m.text, steps: [], done: true, parts: m.components }));
export const ago = (iso: string) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 24 * 60) return `${Math.round(m / 60)} h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/**
 * The conversation with the desk: messages, sending and stopping, saved threads, the plan cards and
 * the desk's own page tools (the fallback ones: any page's own tools come first). Mounted once, by
 * the panel or by the desk page.
 */
export function useDeskChat() {
  const { pathname } = useLocation();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const conv = useRef(storedThread() ?? newId());
  const [threads, setThreadsState] = useState<ThreadRow[] | null>(keptThreads);
  const setThreads = (f: ThreadRow[] | null | ((h: ThreadRow[] | null) => ThreadRow[] | null)) => setThreadsState((h) => { const v = typeof f === 'function' ? f(h) : f; keptThreads = v; return v; });
  const abort = useRef<AbortController | null>(null);
  const lastAsk = useRef(''); // the user's latest message: an amount to invest is honoured only if they named one
  const nav = useNavigate();
  useEffect(() => { setNavigate((p) => nav(p)); }, [nav]);

  // Saved conversations: list, open, delete.
  const loadThread = async (id: string) => {
    const r = await api(`/v1/threads/${encodeURIComponent(id)}`).catch(() => null);
    if (!r?.ok) return false;
    const j = await r.json() as { messages: SavedMsg[] };
    dropPending(); abort.current?.abort();
    conv.current = id; rememberThread(id);
    setMsgs(toMsgs(j.messages));
    return true;
  };
  const restore = () => { const id = storedThread(); if (id) void loadThread(id); };
  const loadThreads = async () => {
    const r = await api('/v1/threads').catch(() => null);
    const rows = r?.ok ? await r.json() as ThreadRow[] : [];
    setThreads(rows);
    return rows;
  };
  const newChat = () => { dropPending(); abort.current?.abort(); setMsgs([]); conv.current = newId(); rememberThread(conv.current); };
  const deleteThread = async (id: string) => {
    const r = await api(`/v1/threads/${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => null);
    if (!r?.ok) return;
    setThreads((h) => h?.filter((t) => t.id !== id) ?? null);
    if (id === conv.current) newChat();
  };
  // The latest desk message, changed in place (streamed text, steps, proposal cards, cards).
  const patchLast = (f: (d: DeskMsg) => DeskMsg) =>
    setMsgs((m) => { const n = [...m]; const last = n[n.length - 1]; if (last?.role === 'desk') n[n.length - 1] = f(last); return n; });
  const patchCard = (id: string, f: (c: Proposal) => Proposal) =>
    setMsgs((m) => m.map((x) => (x.role === 'desk' && x.cards?.some((c) => c.id === id) ? { ...x, cards: x.cards.map((c) => (c.id === id ? f(c) : c)) } : x)));
  /** A card into the conversation: the latest answer's, or a message of its own when none is open. */
  const addCard = (card: DeskCardData) => setMsgs((m) => {
    const n = [...m]; const last = n[n.length - 1];
    if (last?.role === 'desk') n[n.length - 1] = { ...last, parts: [...(last.parts ?? []), card] };
    else n.push({ role: 'desk', text: '', steps: [], done: true, parts: [card] });
    return n;
  });
  const answers = useRef(new Map<string, (r: Record<string, unknown>) => void>());
  const capitalAnswer = useRef<((v: { capital: number; riskPct: number } | null) => void) | null>(null);
  const turnLevels = useRef<{ price: number; label: string; kind: string }[]>([]);
  const decide = async (c: Proposal, place: boolean, now = false) => {
    const done = answers.current.get(c.id);
    if (!place) { patchCard(c.id, (x) => ({ ...x, state: 'dismissed' })); answers.current.delete(c.id); done?.({ placed: false, reason: 'the user dismissed it' }); return; }
    patchCard(c.id, (x) => ({ ...x, state: 'placing', error: undefined }));
    const a = c.a;
    const r = await api('/v1/paper', { method: 'POST', body: JSON.stringify({ symbol: a.symbol, side: a.side, entry: a.entry_type, price: a.entry_type === 'market' ? 0 : a.entry, stop: a.stop, target: a.target, qty: a.qty, horizon: a.horizon, setup: a.setup, note: a.tested, ...(a.trailing ? { trail: 'breakout' } : {}), ...(a.trailing && now ? { now: true } : {}) }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { patchCard(c.id, (x) => ({ ...x, state: 'pending', error: j.error ?? 'Could not place it' })); return; }
    patchCard(c.id, (x) => ({ ...x, state: 'placed' }));
    answers.current.delete(c.id);
    done?.({ placed: true, id: j.id, status: j.state?.status ?? 'waiting', ...(a.trailing ? { entry: now ? 'now: at the next 5-minute candle’s open, an entry timing the rule’s tested record doesn’t cover (the review compares it with the next open)' : 'the next session’s open (the tested entry)' } : {}), note: 'Placed as a paper trade; it plays out on live 5-minute candles (Journal → Paper trades).' });
  };
  // Stopping or starting over answers any card still waiting (else the run would wait on it forever).
  const dropPending = () => {
    for (const [, done] of answers.current) done({ placed: false, reason: 'the conversation was stopped' });
    answers.current.clear();
    capitalAnswer.current?.(null); capitalAnswer.current = null;
  };
  const giveCapital = (capital: number, riskPct: number) => {
    try { localStorage.setItem('mhq.capital', String(capital)); localStorage.setItem('mhq.riskPct', String(riskPct)); } catch { /* this session only */ }
    setMsgs((m) => m.map((x) => (x.role === 'desk' && x.ask && !x.ask.done ? { ...x, ask: { ...x.ask, done: true } } : x)));
    capitalAnswer.current?.({ capital, riskPct }); capitalAnswer.current = null;
  };
  usePageTools({
    read_market: async (a) => {
      const out = await readMarket(String(a.index ?? ''));
      if (!('error' in out)) addCard({ kind: 'board', tool: 'read_market', data: out as unknown as Record<string, unknown> });
      return out;
    },
    plan_paper_trade: async (a) => {
      // Sized here by MoneyHQ's rule from the saved capital and risk: the model never supplies a quantity.
      let capital = 0, riskPct = 1;
      try { capital = Number((localStorage.getItem('mhq.capital') ?? '').replace(/[^\d.]/g, '')); riskPct = Number(localStorage.getItem('mhq.riskPct') ?? '1') || 1; } catch { /* not stored */ }
      if (Number(a.risk_pct) > 0) riskPct = Number(a.risk_pct);
      if (!(capital > 0)) {
        // Ask right here, then carry on with the plan.
        const got = await new Promise<{ capital: number; riskPct: number } | null>((resolve) => {
          const id = newId();
          capitalAnswer.current = resolve;
          patchLast((m) => ({ ...m, ask: { id, done: false } }));
        });
        if (!got) return { error: 'The user did not give their capital; ask them for it.' };
        capital = got.capital;
        if (!(Number(a.risk_pct) > 0)) riskPct = got.riskPct;
      }
      // A two-way squeeze is always planned on both sides (conditional breakout orders), whatever
      // single plan was asked for: one side would be a guess.
      const pageNow = await runPageTool('read_page', {}) as { chart?: { chartPatterns?: { squeezePlans?: Record<string, unknown>[] | null } } };
      const squeeze = pageNow.chart?.chartPatterns?.squeezePlans;
      // (A trailing-stop pick from the scan is its own rule: no squeeze override.)
      const asks = squeeze?.length === 2 && !a.trailing ? squeeze.map((p) => ({ ...p, symbol: a.symbol, horizon: 'intraday' })) : [a];
      // A trade by the breakout rule takes its entry estimate and stop from the rule (code), never the model.
      if (a.trailing) {
        const sym = String(a.symbol ?? '').toUpperCase();
        const r = await api(`/v1/setups/breakout?symbol=${encodeURIComponent(sym)}`).catch(() => null);
        const j = r?.ok ? await r.json() as { ok: boolean; livePrice?: number; lastSession?: string; plan?: { lastClose: number; stop: number; status: string; since?: string } } : null;
        if (!j) return { error: `Couldn't read the breakout rule for ${sym}. Nothing was shown.` };
        if (!j.ok || !j.plan) return { error: j.livePrice
          ? `${sym} has no breakout signal on a finished close (the last was ${j.lastSession}). The rule judges closes: if today closes at a new high, the signal comes then, and the rule enters at the next open. Nothing was shown.`
          : `${sym} isn't in a new-high breakout trade and has no new signal, so the rule has nothing to place. Nothing was shown.` };
        asks[0] = { ...a, entry: j.plan.lastClose, stop: j.plan.stop, rule_status: j.plan.status, rule_since: j.plan.since, amount: namesAmount(lastAsk.current) ? a.amount : undefined };
      }
      const prepared = asks.map((x) => preparePlan(x, capital, riskPct));
      const bad = prepared.find((x) => 'error' in x);
      if (bad) return bad as { error: string };
      // Draw every plan, show every card at once, then wait for the user's decisions.
      const ok = prepared as Exclude<ReturnType<typeof preparePlan>, { error: string }>[];
      // A trade held for days can cross an ex-date: say so on the card and to the desk.
      for (const x of ok) {
        if (x.plan.horizon !== 'swing') continue;
        const note = await exDatesAhead(x.plan.symbol);
        if (note) { x.plan.exNote = note; (x.sized as Record<string, unknown>).ex_dates_ahead = note; }
      }
      for (const x of ok) turnLevels.current.push(...x.levels);
      await runPageTool('mark_chart', { levels: turnLevels.current });
      const decisions = ok.map((x) => new Promise<Record<string, unknown>>((resolve) => {
        const id = newId();
        answers.current.set(id, (r) => resolve({ ...x.sized, ...r }));
        patchLast((m) => ({ ...m, cards: [...(m.cards ?? []), { id, a: x.plan, size: x.size, state: 'pending' }] }));
      }));
      const results = await Promise.all(decisions);
      if (ok.length === 2) return { squeeze: true, note: 'The chart is in a two-way squeeze, so both sides were planned as conditional breakout orders; only one can trigger (the other expires with the day).', plans: results };
      return results[0];
    },
    read_page: () => ({ page: pathname, note: 'No stock chart is open. Use show_chart with a symbol to open one.' }),
    show_chart: async (a) => {
      if (!a.symbol) return { error: 'No stock is open: give a symbol' };
      await openStock(String(a.symbol));
      return runPageTool('show_chart', a);
    },
    mark_chart: () => ({ error: 'No chart is open' }),
  }, { fallback: true });

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput(''); setBusy(true); turnLevels.current = []; lastAsk.current = q; rememberThread(conv.current);
    setMsgs((m) => [...m, { role: 'user', text: q }, { role: 'desk', text: '', steps: [], done: false }]);
    const patch = patchLast;
    const on = (e: DeskEvent) => {
      if (e.type === 'text') patch((d) => ({ ...d, text: d.text + e.delta }));
      else if (e.type === 'retract') patch((d) => ({ ...d, text: d.text.endsWith(e.text) ? d.text.slice(0, d.text.length - e.text.length) : d.text }));
      else if (e.type === 'step') patch((d) => ({ ...d, steps: [...d.steps, stepLabel(e.name, e.args)] }));
      else if (e.type === 'component') patch((d) => ({ ...d, parts: [...(d.parts ?? []), e.card] }));
      else patch((d) => ({ ...d, error: e.message }));
    };
    const ac = new AbortController(); abort.current = ac;
    try { await ask(conv.current, q, on, ac.signal); }
    catch (e) { if (!ac.signal.aborted) on({ type: 'error', message: e instanceof Error ? e.message : 'The desk is unavailable right now.' }); }
    finally { patch((d) => ({ ...d, done: true })); setBusy(false); abort.current = null; if (threads) void loadThreads(); }
  };
  const stop = () => { dropPending(); abort.current?.abort(); };

  // New users (no paper trades yet) start with the guided first trade.
  const { rows: paperRows } = usePaperTrades();
  const firstTimer = paperRows !== null && paperRows.length === 0;
  const onStock = pathname.startsWith('/stock/');
  const suggestions = [
    ...(firstTimer ? [GUIDE_START] : []),
    ...(onStock
      ? ['What pattern is this chart forming?', 'Is there a tested entry here?', 'Plan a paper trade with my capital and risk']
      : ['I have ₹1 lakh. What is the right stock?', 'How is the market today?']),
  ];
  return { msgs, input, setInput, busy, send, stop, newChat, loadThread, restore, threads, loadThreads, deleteThread, conv, decide, giveCapital, addCard, suggestions, onStock, firstTimer };
}
export type DeskChat = ReturnType<typeof useDeskChat>;

/** The conversation's messages: the user's, and the desk's (steps, cards, words, follow-ups, plans). */
export function DeskMessages({ chat, act, wide, chartProps }: { chat: DeskChat; act: CardActions; wide?: boolean; chartProps?: ChartProps }) {
  const { msgs } = chat;
  // Plan marks go on the latest chart card only.
  let lastChart = '';
  msgs.forEach((m, i) => { if (m.role === 'desk') m.parts?.forEach((c, j) => { if (c.kind === 'chart') lastChart = `${i}-${j}`; }); });
  return (
    <>
      {msgs.map((m, i) => m.role === 'user' ? (
        <div key={i} className={cn('rounded-2xl rounded-br-sm bg-brand-soft px-3.5 py-2 text-ink', wide ? 'ml-auto max-w-[80%] w-fit' : 'ml-8')}>{m.text}</div>
      ) : (
        <div key={i} className="space-y-2" data-msg="desk">
          {m.steps.length > 0 && (
            <ul className="space-y-0.5 text-[12px] text-muted-foreground">
              {m.steps.map((s, j) => <li key={j} className="flex items-center gap-1.5"><span className="size-1 rounded-full bg-muted-foreground/60" />{s}</li>)}
            </ul>
          )}
          {m.parts?.map((c, j) => <DeskCard key={j} card={c} act={act} chart={chartProps && { onReady: chartProps.onReady, marks: `${i}-${j}` === lastChart ? chartProps.marks : undefined }} />)}
          {(() => { const { body, follow } = splitFollowUps(m.text); return (<>
            {body ? <Rich text={body} /> : !m.done && !m.error && !m.cards?.length && !m.ask && <p className="animate-pulse text-muted-foreground">Thinking…</p>}
            {m.done && follow.length > 0 && i === msgs.length - 1 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {follow.map((f) => <button key={f} type="button" onClick={() => void chat.send(f)} className="rounded-full border px-2.5 py-1 text-left text-[12px] text-muted-foreground hover:border-brand-strong hover:text-brand-strong">{f}</button>)}
              </div>
            )}
          </>); })()}
          {m.ask && <CapitalCard done={m.ask.done} onSave={chat.giveCapital} />}
          {m.cards?.map((c) => <ProposalCard key={c.id} c={c} onDecide={(place, now) => void chat.decide(c, place, now)} />)}
          {m.error && <p className="text-[12.5px] text-bear">{m.error}</p>}
        </div>
      ))}
    </>
  );
}

/** The message box: Enter sends, Shift+Enter is a new line; Stop while the desk answers. */
export function DeskComposer({ chat, placeholder, inputRef, big, above }: { chat: DeskChat; placeholder: string; inputRef?: React.RefObject<HTMLTextAreaElement | null>; big?: boolean; above?: React.ReactNode }) {
  const { input, setInput, busy, send, stop } = chat;
  // The box grows with what is typed, up to a few lines.
  const own = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef ?? own;
  useEffect(() => { const el = ref.current; if (!el) return; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, big ? 200 : 128)}px`; }, [input, big, ref]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); void send(input); }}>
      {above}
      <div className={cn('flex items-end gap-2 border bg-background focus-within:border-brand-strong', big ? 'rounded-2xl px-4 py-3 shadow-sm' : 'rounded-xl px-3 py-2')}>
        <textarea ref={ref} rows={1} value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input); } }}
          className={cn('flex-1 resize-none bg-transparent leading-6 outline-none', big ? 'py-1.5 text-[16px] lg:text-[15px]' : 'py-0.5 text-[16px] lg:text-[13.5px]')} aria-label="Message the desk" />
        {busy
          ? <Button type="button" size="icon" variant="outline" className={cn('rounded-full', big ? 'size-9' : 'size-7')} aria-label="Stop" onClick={stop}><SquareIcon className="size-3" /></Button>
          : <Button type="submit" size="icon" className={cn('rounded-full', big ? 'size-9' : 'size-7')} aria-label="Send" disabled={!input.trim()}><ArrowUpIcon className="size-4" /></Button>}
      </div>
    </form>
  );
}

/** The docked panel (every page but /desk). */
export function Desk() {
  const isOpen = useOpen();
  const { phone, keyboard, sheet } = usePhoneSheet(isOpen);
  const chat = useDeskChat();
  const { msgs, busy, send, onStock, suggestions } = chat;
  const [showHistory, setShowHistory] = useState(false);
  const restored = useRef(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nav = useNavigate();
  // Reopen the last conversation the first time the desk opens.
  useEffect(() => { if (isOpen && !restored.current) { restored.current = true; chat.restore(); } }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleHistory = () => { if (showHistory) setShowHistory(false); else { setShowHistory(true); void chat.loadThreads(); } };
  // A card's chart opens the stock page here (the desk page draws it in the conversation instead).
  const act: CardActions = { ask: (q) => void send(q), chart: (sym) => { void openStock(sym); } };

  // Dock beside the page on wide screens (the page and header leave room via --desk-w).
  useEffect(() => {
    const apply = () => document.documentElement.style.setProperty('--desk-w', isOpen && innerWidth >= 1280 ? '400px' : '0px');
    apply(); addEventListener('resize', apply);
    return () => { removeEventListener('resize', apply); document.documentElement.style.setProperty('--desk-w', '0px'); };
  }, [isOpen]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') { e.preventDefault(); if (open) closeDesk(); else openDesk(); } };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs]);
  // Laptops: focus the input on open. Phones: don't (the keyboard would cover the sheet at once).
  useEffect(() => { if (isOpen && !phone) inputRef.current?.focus(); }, [isOpen, phone]);
  // A question handed over by openDesk (e.g. the chart's Ask button).
  useEffect(() => { if (isOpen && queued && !busy) { const q = queued; queued = null; void send(q); } });

  return (
    <>
      {!isOpen && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={() => openDesk()} className="fixed right-5 bottom-5 z-40 h-11 gap-2 rounded-full border bg-background px-4 text-ink shadow-lg hover:bg-background hover:text-ink dark:bg-background dark:hover:bg-background max-lg:hidden" aria-label="Ask MoneyHQ">
              <span className="text-primary"><DeskIcon className="size-4" /></span> Ask MoneyHQ
            </Button>
          </TooltipTrigger>
          <TooltipContent>Ask about this page (⌘J)</TooltipContent>
        </Tooltip>
      )}
      {/* Phones and tablets: the desk's floating button, above the tab bar. */}
      {!isOpen && (
        <button type="button" onClick={() => openDesk()} aria-label="Ask MoneyHQ"
          className="fixed right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-40 inline-flex h-11 items-center gap-2 rounded-full border bg-background px-4 text-sm font-medium text-ink shadow-lg active:scale-95 lg:hidden">
          {/* The laptop's Ask MoneyHQ pill, in size: h-11, px-4, a 16 px coin, text-sm. */}
          <span className="text-primary"><DeskIcon className="size-4" /></span>Ask MoneyHQ
        </button>
      )}
      {/* Phones: a bottom sheet over a dimmed page (a tap outside closes it). */}
      {isOpen && phone && <div aria-hidden className="fixed inset-0 z-[49] bg-black/30 animate-in fade-in-0" onClick={closeDesk} />}
      <aside aria-label="MoneyHQ desk" aria-hidden={!isOpen} style={sheet}
        className={cn('fixed inset-y-0 right-0 z-50 flex w-full max-w-[400px] flex-col border-l bg-card shadow-xl transition-transform lg:pt-[env(safe-area-inset-top)] lg:pb-[env(safe-area-inset-bottom)] xl:z-40 xl:shadow-none',
          'max-lg:top-auto max-lg:max-w-none max-lg:rounded-t-2xl max-lg:border-t max-lg:border-l-0',
          isOpen ? 'translate-x-0 max-lg:animate-in max-lg:slide-in-from-bottom' : 'translate-x-full max-lg:hidden')}>
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30 lg:hidden" aria-hidden />
        <div className="flex h-12 shrink-0 items-center justify-between border-b px-4 lg:h-14">
          <div className="flex items-center gap-2">
            <DeskIcon className="size-4 text-brand-strong" />
            <span className="font-heading text-[15px] font-semibold">Desk</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">paper trading</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className={cn('h-7 gap-1 text-[12px] text-muted-foreground', showHistory && 'bg-muted text-ink')} aria-pressed={showHistory} onClick={toggleHistory}><HistoryIcon className="size-3.5" />History</Button>
            {msgs.length > 0 && <Button variant="ghost" size="sm" className="h-7 text-[12px] text-muted-foreground" onClick={() => { chat.newChat(); setShowHistory(false); }}>New chat</Button>}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="size-7" aria-label="Open the desk page" onClick={() => { closeDesk(); nav('/desk'); }}><Maximize2Icon className="size-3.5" /></Button>
              </TooltipTrigger>
              <TooltipContent>Open the desk page</TooltipContent>
            </Tooltip>
            <Button variant="ghost" size="icon" className="size-7" aria-label="Close the desk" onClick={closeDesk}><XIcon className="size-4" /></Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 text-[13.5px] leading-relaxed">
          {showHistory && (
            <ThreadList chat={chat} onOpen={(id) => { void chat.loadThread(id).then(() => setShowHistory(false)); }} />
          )}
          {!showHistory && msgs.length === 0 && (
            <div className="space-y-3 pt-2">
              <p className="text-muted-foreground">I can see this page and work the chart with you: what it’s forming, which setups MoneyHQ has tested, and a paper plan sized to your capital and risk. Every level I give comes from MoneyHQ’s engine, with its tested record.</p>
              <div className="flex flex-col items-start gap-2">
                {suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => void send(s)} className="rounded-full border px-3 py-1.5 text-left text-[12.5px] hover:border-brand-strong hover:text-brand-strong">{s}</button>
                ))}
              </div>
            </div>
          )}
          {!showHistory && <DeskMessages chat={chat} act={act} />}
          <div ref={endRef} />
        </div>
        <div className="shrink-0 border-t p-3">
          <DeskComposer chat={chat} inputRef={inputRef} placeholder={onStock ? 'Ask about this chart…' : 'Ask MoneyHQ…'} />
          {/* Over the keyboard every line counts; the note shows again when it closes. */}
          {!keyboard && <p className="mt-1.5 text-center text-[10.5px] text-muted-foreground">Technical analysis for paper trading, not investment advice.</p>}
        </div>
      </aside>
    </>
  );
}

/** The user's saved conversations (latest first): open one, or delete it. */
export function ThreadList({ chat, onOpen, dense }: { chat: DeskChat; onOpen: (id: string) => void; dense?: boolean }) {
  const { threads } = chat;
  return (
    <div className="space-y-1">
      {!dense && <p className="mb-2 text-xs text-muted-foreground">Your conversations with the desk (latest first). They're saved to your account.</p>}
      {threads === null && <p className="text-[12.5px] text-muted-foreground">Loading…</p>}
      {threads?.length === 0 && <p className="text-[12.5px] text-muted-foreground">No saved conversations yet.</p>}
      {threads?.map((t) => (
        <div key={t.id} className={cn('group flex items-center gap-2 rounded-lg hover:bg-muted/60', dense ? 'px-2 py-1.5' : 'border px-3 py-2', t.id === chat.conv.current && (dense ? 'bg-muted' : 'border-brand-strong/40'))}>
          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpen(t.id)}>
            <span className={cn('block truncate', dense ? 'text-[13px] text-ink' : 'font-medium')}>{t.title}</span>
            {!dense && <span className="text-[11px] text-muted-foreground">{ago(t.updatedAt)}</span>}
          </button>
          <Button variant="ghost" size="icon" className="size-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 max-lg:opacity-60" aria-label={`Delete "${t.title}"`} onClick={() => { if (confirm('Delete this conversation?')) void chat.deleteThread(t.id); }}><Trash2Icon className="size-3.5" /></Button>
        </div>
      ))}
    </div>
  );
}

/** One plan, sized by MoneyHQ's rule; an error if its levels don't make sense for its side. */
function preparePlan(a: Record<string, unknown>, capital: number, riskPct: number) {
  if (a.trailing) return prepareTrailing(a, capital, riskPct);
  const side: Proposal['a']['side'] = a.side === 'short' ? 'short' : 'long', d = side === 'long' ? 1 : -1;
  const entry = Number(a.entry), stop = Number(a.stop), target = Number(a.target);
  if (!(entry > 0 && stop > 0 && target > 0)) return { error: 'entry, stop and target must be positive prices from a tool' };
  if (d * (entry - stop) <= 0 || d * (target - entry) <= 0) return { error: `For a ${side}, the stop must be ${side === 'long' ? 'below' : 'above'} the entry and the target ${side === 'long' ? 'above' : 'below'} it` };
  const size = positionSize(capital, riskPct, entry, Math.abs(entry - stop));
  if (!size) return { error: 'The risk per share is too large for this capital and risk: not even one share fits' };
  const rr = Math.round((Math.abs(target - entry) / Math.abs(entry - stop)) * 100) / 100;
  const entryType = (['limit', 'stop', 'market'].includes(String(a.entry_type)) ? a.entry_type : 'limit') as Proposal['a']['entry_type'];
  const plan: Proposal['a'] = { symbol: String(a.symbol).toUpperCase(), side, entry_type: entryType, entry, stop, target, qty: size.qty,
    horizon: side === 'short' ? 'intraday' : a.horizon === 'swing' ? 'swing' : 'intraday', setup: String(a.setup ?? ''), tested: String(a.tested ?? '') };
  return {
    plan, size: { atRisk: size.atRisk, value: size.value, rr, capital, riskPct, capped: size.capped },
    levels: [{ price: entry, label: `${side === 'long' ? 'Buy' : 'Sell'} ${entryType === 'stop' ? (side === 'long' ? 'above' : 'below') : 'at'}`, kind: 'entry' }, { price: stop, label: 'Stop', kind: 'stop' }, { price: target, label: 'Target', kind: 'target' }],
    sized: { symbol: plan.symbol, side, setup: plan.setup, tested: plan.tested, entry, stop, target, qty: size.qty, money_at_risk: inrText(size.atRisk), risk_pct_of_capital: pct2(size.atRisk / capital * 100), position_value: inrText(size.value), reward_risk: rr, capital: inrText(capital), risk_limit_pct: riskPct, capped_by_capital: size.capped },
  };
}

/** The user's message names a sum of money ("put ₹50,000", "2 lakh", "Rs 20000"): only then is an amount from the desk used. */
const namesAmount = (q: string) => /₹|\brs\.?\s*\d|\brupees?\b|\d\s*(lakh|lac|k|crore|cr)\b|\b\d{4,}\b|\d{1,3}(,\d{2,3})+/i.test(q);

/** The market today as the Market Flow board computes it, from any page. */
async function readMarket(indexId: string) {
  const ix = marketIndex(indexId);
  const r = await api(`/v1/session?index=${encodeURIComponent(ix.id)}`).catch(() => null);
  if (!r?.ok) return { error: 'The market session isn\'t available right now.' };
  return marketReading(await r.json() as Session, ix);
}

/** Ex-dates in the next 60 days, in words for the plan card ("" when none or unknown). */
async function exDatesAhead(symbol: string): Promise<string> {
  try {
    const r = await api(`/v1/corporate-actions?symbol=${encodeURIComponent(symbol)}`);
    if (!r.ok) return '';
    const j = await r.json() as { upcoming?: CorpAction[] };
    const soon = (j.upcoming ?? []).filter((x) => Date.parse(x.exDate) - Date.now() < 60 * 86400000);
    if (!soon.length) return '';
    const day = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    return 'Ex-date ahead: ' + soon.map((x) => {
      const what = actionLabel(x) ?? x.subject;
      if (x.kind === 'dividend') return `${what} on ${day(x.exDate)} (the price drops by about the dividend that day; the dividend is paid to holders)`;
      if (x.kind === 'split' || x.kind === 'bonus') return `${what} on ${day(x.exDate)} (prices adjust; the paper trade is adjusted with them)`;
      return `${what} on ${day(x.exDate)}`;
    }).join('; ') + '.';
  } catch { return ''; }
}

const ruleNote = (a: Record<string, unknown>) => a.rule_status === 'in-trend'
  ? `MoneyHQ's rule: in a breakout trade since ${a.rule_since}; the stop is that trade's trailing stop now, the entry estimate the last close`
  : "MoneyHQ's rule: a new signal on the latest close; the starting stop, the entry estimate the last close";

/** A new-high breakout pick played by the rule: long, next open, trailing stop, no target. */
function prepareTrailing(a: Record<string, unknown>, capital: number, riskPct: number) {
  const entry = Number(a.entry), stop = Number(a.stop);
  if (!(entry > 0 && stop > 0 && stop < entry)) return { error: 'For a trailing-stop trade, give the last close as entry and the trailing stop (below it) as stop' };
  // By the amount asked for; else by risk; and when the rule's wide stop leaves no shares at the
  // user's risk, by an equal split of the capital across 5 picks (as the scan shows), said plainly.
  let amount = Number(a.amount) || 0, sizedBy = amount > 0 ? `the amount asked for (${inrText(amount)})` : `${riskPct}% risk`;
  let size = amount > 0 ? null : positionSize(capital, riskPct, entry, entry - stop);
  if (!size && !(amount > 0)) { amount = capital / 5; sizedBy = `an equal split (${inrText(amount)}): at ${riskPct}% risk this rule's wide stop allows no shares`; }
  if (!size) {
    const q = Math.min(Math.floor(amount / entry), Math.floor(capital / entry));
    if (q < 1) return { error: `${inrText(amount)} doesn't buy one share at ${inrText(entry)}. Nothing was shown or placed.` };
    size = { qty: q, atRisk: q * (entry - stop), value: q * entry, capped: false };
  }
  const plan: Proposal['a'] = { symbol: String(a.symbol).toUpperCase(), side: 'long', entry_type: 'market', entry, stop, target: 0, qty: size.qty, horizon: 'swing',
    setup: String(a.setup || 'new-high breakout (trailing stop)'), tested: String(a.tested ?? ''), trailing: true, sizedBy };
  return {
    plan, size: { atRisk: size.atRisk, value: size.value, rr: 0, capital, riskPct, capped: size.capped },
    levels: [{ price: entry, label: 'Buy at next open (≈)', kind: 'entry' }, { price: stop, label: 'Trailing stop', kind: 'stop' }],
    sized: { symbol: plan.symbol, side: 'long', setup: plan.setup, tested: plan.tested, levels_from: ruleNote(a), sized_by: sizedBy, entry_estimate: entry, starting_stop: stop, trailing: 'highest close since entry - 10 x ATR(20), only rising; exit at the next open after a close below it', qty: size.qty, money_at_risk_at_start: inrText(Math.round(size.atRisk * 100) / 100), risk_pct_of_capital: pct2(size.atRisk / capital * 100), position_value: inrText(Math.round(size.value * 100) / 100), capital: inrText(capital), card_shown: true },
  };
}

/** "Follow-ups: a | b | c" on the reply's last line becomes buttons. */
function splitFollowUps(text: string): { body: string; follow: string[] } {
  const m = text.match(/\n?\s*\**Follow-ups:?\**:?\s*(.+)\s*$/i);
  if (!m || m.index === undefined) return { body: text, follow: [] };
  return { body: text.slice(0, m.index).trimEnd(), follow: m[1].split('|').map((x) => x.replace(/^[\s"“*]+|[\s"”*]+$/g, '')).filter(Boolean).slice(0, 3) };
}

function CapitalCard({ done, onSave }: { done: boolean; onSave: (capital: number, riskPct: number) => void }) {
  const [cap, setCap] = useState('');
  const [risk, setRisk] = useState('1');
  const n = Number(cap.replace(/[^\d.]/g, ''));
  if (done) return <p className="text-[12px] text-muted-foreground">Capital saved in this browser.</p>;
  return (
    <form className="rounded-xl border bg-background p-3 text-[12.5px]" onSubmit={(e) => { e.preventDefault(); if (n > 0) onSave(n, Number(risk)); }}>
      <p className="mb-2 font-semibold">To size the plan, what do you trade with?</p>
      <label className="flex items-center gap-2">
        <span className="text-muted-foreground">Capital ₹</span>
        <input autoFocus inputMode="numeric" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="e.g. 500000" aria-label="Trading capital"
          className="num h-7 w-32 rounded-md border bg-card px-2 outline-none focus:border-brand-strong" />
      </label>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="text-muted-foreground">Risk per trade</span>
        {['0.5', '1', '2'].map((r) => (
          <button key={r} type="button" onClick={() => setRisk(r)} className={cn('rounded-full border px-2 py-0.5 text-[12px]', risk === r && 'border-brand-strong bg-brand-soft text-brand-strong')}>{r}%</button>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Kept in this browser, like the trade plan’s position-size box.{n > 0 && ` Most you’d lose on one trade: ₹${Math.round(n * Number(risk) / 100).toLocaleString('en-IN')}.`}</p>
      <Button type="submit" size="sm" className="mt-2 h-7 rounded-full px-3 text-[12px]" disabled={!(n > 0)}>Continue</Button>
    </form>
  );
}

/** The session is running (IST, weekdays 09:15 to 15:25): a trade by the rule can be entered now.
 * (A holiday is caught by the API, which needs live prices for it.) */
function sessionRunning() {
  const ist = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  const m = ist.getHours() * 60 + ist.getMinutes(), d = ist.getDay();
  return d >= 1 && d <= 5 && m >= 9 * 60 + 15 && m < 15 * 60 + 25;
}

function ProposalCard({ c, onDecide }: { c: Proposal; onDecide: (place: boolean, now: boolean) => void }) {
  const a = c.a, long = a.side === 'long';
  // A trade by the rule: at the next open (tested), or now (the next 5-minute candle), while the session runs.
  const [now, setNow] = useState(false);
  const canNow = !!a.trailing && sessionRunning();
  const row = (k: string, v: React.ReactNode, cls = '') => <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className={cn('num font-medium', cls)}>{v}</span></div>;
  const entry = a.trailing ? (now ? `now: the next 5-minute candle’s open (≈ ₹${NSE.formatPrice(a.entry)})` : `next session’s open (≈ ₹${NSE.formatPrice(a.entry)})`) : a.entry_type === 'market' ? 'next candle’s open' : `${a.entry_type === 'stop' ? 'on a break of' : 'limit'} ₹${NSE.formatPrice(a.entry)}`;
  // The pre-trade checklist: the trader answers three questions before Place (the answers are the
  // card's own numbers, so ticking them is reading them).
  const [ticks, setTicks] = useState([false, false, false]);
  const checks = [
    { q: 'Where am I wrong?', a: a.trailing ? `On a close below the trailing stop (starts at ₹${NSE.formatPrice(a.stop)}).` : `At ₹${NSE.formatPrice(a.stop)}: if price ${long ? 'falls' : 'rises'} there, the plan is wrong and the stop exits.` },
    { q: 'What do I lose?', a: `${inrText(c.size.atRisk)} if the stop fills (${pct2(c.size.atRisk / c.size.capital * 100)}% of capital), more if price gaps through it.` },
    { q: 'Is this a tested setup?', a: a.trailing ? (now ? 'The rule is (it beat a placebo in MoneyHQ’s tests), but entering now isn’t its tested timing: it enters at the next open.' : 'Yes: the new-high breakout rule, which beat a placebo in MoneyHQ’s tests.') : 'No: it isn’t one of MoneyHQ’s tested rules. Practice only; its result says nothing about an edge.' },
  ];
  const ready = ticks.every(Boolean);
  return (
    <div className="rounded-xl border bg-background p-3 text-[12.5px]">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">Paper trade · {a.symbol} <span className={long ? 'text-bull' : 'text-bear'}>{a.side}</span></span>
        <span className="text-[11px] text-muted-foreground">{a.horizon}</span>
      </div>
      <div className="space-y-1">
        {row('Plan', a.setup)}
        {canNow && (c.state === 'pending' || c.state === 'placing') && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Enter</span>
            <span className="inline-flex rounded-full border p-0.5 text-[11.5px]" role="radiogroup" aria-label="When to enter">
              {[[false, 'Next open · tested'], [true, 'Now']].map(([v, l]) => (
                <button key={String(v)} type="button" role="radio" aria-checked={now === v} onClick={() => setNow(v as boolean)}
                  className={cn('rounded-full px-2.5 py-0.5', now === v ? 'bg-brand-soft font-medium text-brand-strong' : 'text-muted-foreground')}>{l as string}</button>
              ))}
            </span>
          </div>
        )}
        {row('Entry', entry)}
        {now && <p className="text-[11px] text-warn-ink">Entering now isn’t the rule’s tested timing: its record is measured from the next session’s open. The review will compare your fill with that open.</p>}
        {row(a.trailing ? 'Starting stop' : 'Stop', `₹${NSE.formatPrice(a.stop)}`, 'text-warn')}
        {a.trailing ? row('Exit', 'trailing stop: highest close − 10 × ATR(20)', 'text-muted-foreground') : row('Target', `₹${NSE.formatPrice(a.target)}`, long ? 'text-bull' : 'text-bear')}
        {row('Quantity', `${a.qty.toLocaleString('en-IN')} shares`)}
        {row('Money at risk', `${inrText(c.size.atRisk)} · ${pct2(c.size.atRisk / c.size.capital * 100)}% of ${inrText(c.size.capital)}`)}
        {row('Position value', inrText(c.size.value))}
        {a.sizedBy && <p className="text-[11px] text-muted-foreground">Sized by {a.sizedBy}.</p>}
        {a.exNote && <p className="text-[11px] text-warn-ink">{a.exNote}</p>}
        {c.size.capped && <p className="text-[11px] text-muted-foreground">Capped by your capital: your {c.size.riskPct}% risk limit would allow more shares than ₹{c.size.capital.toLocaleString('en-IN')} buys.</p>}
        {!a.trailing && row('Reward : risk', `${c.size.rr} : 1`)}
      </div>
      {a.tested && <p className="mt-2 text-[11.5px] text-muted-foreground"><b className="text-ink">Tested:</b> {a.tested}</p>}
      {(c.state === 'pending' || c.state === 'placing') && (
        <fieldset className="mt-2 space-y-1.5 rounded-lg border border-dashed p-2">
          <legend className="px-1 text-[11px] font-medium text-muted-foreground">Before you place it</legend>
          {checks.map((x, i) => (
            <label key={x.q} className="flex cursor-pointer items-start gap-2 text-[11.5px]">
              <input type="checkbox" className="mt-0.5 accent-(--color-brand-strong)" checked={ticks[i]} onChange={(e) => setTicks((t) => t.map((v, j) => (j === i ? e.target.checked : v)))} />
              <span><b className="text-ink">{x.q}</b> <span className="text-muted-foreground">{x.a}</span></span>
            </label>
          ))}
        </fieldset>
      )}
      {c.error && <p className="mt-2 text-[11.5px] text-bear">{c.error}</p>}
      <div className="mt-3 flex gap-2">
        {c.state === 'placed' ? <span className="text-[12px] font-medium text-bull">Placed · tracking it in Journal → Paper trades</span>
          : c.state === 'dismissed' ? <span className="text-[12px] text-muted-foreground">Dismissed</span>
          : <>
            <Button size="sm" className="h-7 rounded-full px-3 text-[12px]" disabled={c.state === 'placing' || !ready} title={ready ? undefined : 'Answer the three questions first'} onClick={() => onDecide(true, now)}>{c.state === 'placing' ? 'Placing…' : now ? 'Place now' : 'Place paper trade'}</Button>
            <Button size="sm" variant="ghost" className="h-7 rounded-full px-3 text-[12px]" onClick={() => onDecide(false, false)}>Dismiss</Button>
          </>}
      </div>
    </div>
  );
}

/** The desk's replies: paragraphs, bullet lists, **bold** and code blocks. */
function Rich({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith('```')) {
      const code: string[] = [];
      for (i++; i < lines.length && !lines[i].startsWith('```'); i++) code.push(lines[i]);
      blocks.push(<pre key={i} className="num overflow-x-auto rounded-md bg-muted p-2 text-[11px]">{code.join('\n')}</pre>);
    } else if (/^\s*[-*•] /.test(l)) {
      const items: string[] = [];
      for (; i < lines.length && /^\s*[-*•] /.test(lines[i]); i++) items.push(lines[i].replace(/^\s*[-*•] /, ''));
      i--;
      blocks.push(<ul key={i} className="list-disc space-y-0.5 pl-5">{items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>);
    } else if (/^#{1,4} /.test(l)) blocks.push(<p key={i} className="font-semibold text-ink">{inline(l.replace(/^#+ /, ''))}</p>);
    else if (l.trim()) blocks.push(<p key={i}>{inline(l)}</p>);
  }
  return <div className="space-y-2 text-ink">{blocks}</div>;
}
// **bold**, *italic* and [links](https://…) (links open in a new tab, http(s) only).
const emph = (s: string, k: string) => s.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((p, i) =>
  p.startsWith('**') && p.endsWith('**') && p.length > 4 ? <b key={k + i}>{p.slice(2, -2)}</b>
  : p.length > 2 && p.startsWith('*') && p.endsWith('*') ? <i key={k + i}>{p.slice(1, -1)}</i>
  : <Fragment key={k + i}>{p}</Fragment>);
const inline = (s: string) => {
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let last = 0, m: RegExpExecArray | null, n = 0;
  while ((m = re.exec(s))) {
    out.push(...emph(s.slice(last, m.index), `t${n}-`));
    out.push(<a key={`a${n++}`} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-brand-strong underline decoration-brand-line underline-offset-2 hover:decoration-brand-strong">{emph(m[1], `l${n}-`)}</a>);
    last = m.index + m[0].length;
  }
  out.push(...emph(s.slice(last), 'e-'));
  return out;
};

/**
 * Phones (below lg): the desk is a bottom sheet sized to the visible viewport, so with the keyboard
 * up its input sits right above it (iOS keeps the layout viewport and shrinks the visual one; the
 * sheet follows window.visualViewport).
 */
function usePhoneSheet(open: boolean) {
  const [phone, setPhone] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(max-width: 1023.98px)').matches);
  const [vv, setVv] = useState<{ h: number; top: number; bottom: number; typing: boolean } | null>(null);
  useEffect(() => {
    const mq = matchMedia('(max-width: 1023.98px)');
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  useEffect(() => {
    const v = window.visualViewport;
    if (!open || !phone || !v) { setVv(null); return; }
    // Typing counts too: some iOS browsers shrink innerHeight with the keyboard, so the gap alone can miss it.
    const update = () => setVv({
      h: v.height, top: v.offsetTop, bottom: Math.max(0, window.innerHeight - (v.height + v.offsetTop)),
      typing: document.activeElement instanceof HTMLTextAreaElement || document.activeElement instanceof HTMLInputElement,
    });
    update();
    v.addEventListener('resize', update);
    v.addEventListener('scroll', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      v.removeEventListener('resize', update); v.removeEventListener('scroll', update);
      document.removeEventListener('focusin', update); document.removeEventListener('focusout', update);
    };
  }, [open, phone]);
  if (!phone || !open || !vv) return { phone, keyboard: false, sheet: undefined };
  const keyboard = vv.bottom > 80 || vv.typing; // the keyboard is up: use all the room above it
  // Placed from the top of the visible area, not from the bottom: with the keyboard up iOS keeps the
  // layout viewport full height and scrolls it, and a sheet placed from the bottom stopped short of
  // the keyboard (a blank band, seen on iPhone Chrome 20 Oct).
  const height = keyboard ? vv.h - 8 : Math.round(vv.h * 0.88);
  return {
    phone,
    keyboard,
    sheet: { top: vv.top + vv.h - height, bottom: 'auto', height, paddingBottom: keyboard ? 0 : 'env(safe-area-inset-bottom)' } as React.CSSProperties,
  };
}
